import { useState, useEffect } from 'react';
import { api } from '../utils/api';
import { API_ENDPOINTS } from '../config';
import { SYNTHETIC_ANALYTICS_DATA } from '../utils/analyticsConstants';

export interface AnalyticsPeriod {
  funnel: { swimlane_uid: string; swimlane_name: string; position: number; count: number; conversion: number }[];
  kpis: {
    total_jobs: number;
    total_jobs_delta: number;
    total_activities: number;
    total_activities_delta: number;
    contacts_linked: number;
    notes_count: number;
    notes_count_delta: number;
    pct_completed_activities: number;
    avg_activities_per_job: number;
  };
  jobs_over_time: { period: string; count: number }[];
  activities_by_type: { type: string; period: string; count: number }[];
}

export interface AnalyticsData {
  computed_at: string;
  periods: {
    '7d': AnalyticsPeriod;
    '30d': AnalyticsPeriod;
    '90d': AnalyticsPeriod;
    'all_time': AnalyticsPeriod;
  };
  stale_jobs: {
    uid: string;
    company_name: string;
    job_title: string;
    swimlane_uid: string | null;
    swimlane_name: string | null;
    last_updated: string;
  }[];
  upcoming_activities: {
    uid: string;
    title: string;
    type: string;
    start_date: string | null;
    job_uid: string | null;
    company_name: string | null;
    job_title: string | null;
  }[];
}

export type PeriodKey = '7d' | '30d' | '90d' | 'all_time';

interface UseBoardAnalyticsResult {
  data: AnalyticsData | null;
  isLoading: boolean;
  isDemo: boolean;
  error: string | null;
}

export function useBoardAnalytics(boardId: string | undefined): UseBoardAnalyticsResult {
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDemo, setIsDemo] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!boardId) return;

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    api.get<{ demo: boolean; data: AnalyticsData | null }>(
      API_ENDPOINTS.kanbans.analytics(boardId)
    )
      .then((response) => {
        if (cancelled) return;
        if (response.demo) {
          setIsDemo(true);
          setData(SYNTHETIC_ANALYTICS_DATA as unknown as AnalyticsData);
        } else {
          setIsDemo(false);
          setData(response.data);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.message || 'Failed to load analytics');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [boardId]);

  return { data, isLoading, isDemo, error };
}
